import { Add as AddIcon } from '@mui/icons-material';
import { Chip, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Role, Specialty } from 'shared/types';

import AddRoleSpecialtyPanel from './AddRoleSpecialtyPanel';
import { roleSpecialtyKey, roleSpecialtyLabel } from './helpers';
import { RoleSpecialtyOption, RolesSpecialtiesSelectorProps } from './types';

type EditState =
    | { kind: 'none' }
    // `index` tracks the entry being built for this add session (null until the
    // first valid selection is appended). The add flow only ever touches its own
    // entry — it never edits or merges into an entry that already existed.
    | { kind: 'add'; index: number | null }
    | { kind: 'edit'; index: number };

type Draft = { role: Role | null; specialty: Specialty | null };

const EMPTY_DRAFT: Draft = { role: null, specialty: null };

function toOption(draft: Draft): RoleSpecialtyOption | null {
    if (draft.specialty) {
        return { role: draft.role, specialty: draft.specialty };
    }

    if (draft.role) {
        return { role: draft.role, specialty: null };
    }

    return null;
}

function RolesSpecialtiesSelector({ value, onChange, disabled }: RolesSpecialtiesSelectorProps) {
    const { t } = useTranslation('rolesSpecialtiesSelector');
    const [ edit, setEdit ] = useState<EditState>({ kind: 'none' });
    const [ draft, setDraft ] = useState<Draft>(EMPTY_DRAFT);

    const close = useCallback(() => {
        setEdit({ kind: 'none' });
        setDraft(EMPTY_DRAFT);
    }, []);

    const openAdd = useCallback(() => {
        setEdit({ kind: 'add', index: null });
        setDraft(EMPTY_DRAFT);
    }, []);

    const openEdit = useCallback((index: number) => {
        setEdit({ kind: 'edit', index });
        setDraft({ role: value[index].role, specialty: value[index].specialty });
    }, [ value ]);

    const handleDelete = useCallback((index: number) => {
        onChange(value.filter((_, i) => i !== index));

        if (edit.kind === 'none') {
            return;
        }

        if (edit.index === index) {
            // The entry currently being edited / built was removed.
            if (edit.kind === 'edit') {
                close();
            } else {
                setEdit({ kind: 'add', index: null });
            }

            return;
        }

        // Removing an earlier entry shifts everything after it down by one — keep the
        // active index pointing at the same entry.
        if (edit.index !== null && index < edit.index) {
            setEdit(edit.kind === 'edit'
                ? { kind: 'edit', index: edit.index - 1 }
                : { kind: 'add', index: edit.index - 1 });
        }
    }, [ value, onChange, edit, close ]);

    // Applies the draft to the committed value immediately, so a selection is never
    // lost by forgetting to confirm. The add flow only ever builds its own new entry;
    // it never edits or merges into an entry that already existed — that only happens
    // when the user explicitly opens a chip for editing.
    const applyDraft = useCallback((next: Draft) => {
        setDraft(next);

        const option = toOption(next);

        if (edit.kind === 'add') {
            if (!option) {
                // Selection cleared — remove the in-progress entry if we had added one.
                if (edit.index !== null) {
                    onChange(value.filter((_, i) => i !== edit.index));
                    setEdit({ kind: 'add', index: null });
                }

                return;
            }

            // A pair identical to a different existing entry must not be duplicated and
            // must not overwrite that entry — leave the list as it is.
            const isDuplicate = value.some(
                (v, i) => i !== edit.index && roleSpecialtyKey(v) === roleSpecialtyKey(option)
            );

            if (edit.index === null) {
                // First valid selection of this add session: append a fresh entry.
                if (!isDuplicate) {
                    onChange([ ...value, option ]);
                    setEdit({ kind: 'add', index: value.length });
                }

                return;
            }

            if (isDuplicate) {
                // The selection now matches another existing entry — drop our
                // in-progress entry so nothing is duplicated, and wait for a fresh,
                // unique selection to start a new entry.
                onChange(value.filter((_, i) => i !== edit.index));
                setEdit({ kind: 'add', index: null });

                return;
            }

            // Update our own in-progress entry in place; never touch the others.
            onChange(value.map((v, i) => (i === edit.index ? option : v)));

            return;
        }

        if (edit.kind === 'edit') {
            if (!option) {
                // Both sides cleared — drop the entry.
                onChange(value.filter((_, i) => i !== edit.index));
                close();

                return;
            }

            // Editing this entry into a pair that already exists elsewhere merges them:
            // apply the change here and drop the other duplicate, keeping this one focused.
            const optionKey = roleSpecialtyKey(option);
            const next: RoleSpecialtyOption[] = [];
            let nextIndex = edit.index;

            value.forEach((v, i) => {
                if (i === edit.index) {
                    nextIndex = next.length;
                    next.push(option);
                } else if (roleSpecialtyKey(v) !== optionKey) {
                    next.push(v);
                }
            });

            onChange(next);

            if (nextIndex !== edit.index) {
                setEdit({ kind: 'edit', index: nextIndex });
            }
        }
    }, [ edit, value, onChange, close ]);

    // Changing the role re-scopes (and resets) the speciality selection.
    const handleRoleChange = useCallback((role: Role | null) => {
        applyDraft({ role, specialty: null });
    }, [ applyDraft ]);

    const handleSpecialtyChange = useCallback((specialty: Specialty | null) => {
        applyDraft({ ...draft, specialty });
    }, [ applyDraft, draft ]);

    // Highlight the entry under the open panel — the one being edited, or the
    // in-progress entry being built while adding (null until the first selection).
    const activeIndex = edit.kind === 'none' ? null : edit.index;

    return (
        <Stack gap={ 2 }>
            <ChipsContainer>
                { value.map((v, i) => (
                    <Chip
                        // Index key: entries are edited in place / appended, never reordered,
                        // so this stays stable and avoids collisions while a pair is mid-edit.
                        key={ i }
                        variant={ i === activeIndex ? 'filled' : 'outlined' }
                        color={ i === activeIndex ? 'primary' : 'default' }
                        label={ roleSpecialtyLabel(v, t) }
                        onClick={ disabled ? undefined : () => openEdit(i) }
                        onDelete={ disabled ? undefined : () => handleDelete(i) }
                    />
                )) }
                { edit.kind !== 'add' && (
                    <AddTriggerChip
                        icon={ <AddIcon fontSize="small" /> }
                        label={ t('addTrigger') }
                        variant="outlined"
                        disabled={ disabled }
                        onClick={ openAdd }
                    />
                ) }
            </ChipsContainer>
            { edit.kind !== 'none' && (
                <AddRoleSpecialtyPanel
                    isEditing={ edit.kind === 'edit' }
                    role={ draft.role }
                    specialty={ draft.specialty }
                    onRoleChange={ handleRoleChange }
                    onSpecialtyChange={ handleSpecialtyChange }
                    onClose={ close }
                    disabled={ disabled }
                />
            ) }
        </Stack>
    );
}

const ChipsContainer = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center'
}));

const AddTriggerChip = styled(Chip)({
    borderStyle: 'dashed'
});

export default memo(RolesSpecialtiesSelector);
